import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { formatTimeBerlin, safeFormatDate } from '../lib/dateTime';
import { formatHoursToHM } from '../lib/formatTime';
import { getISOWeek, parseISO } from 'date-fns';
import {
  Users2,
  RefreshCw,
  Info,
  ExternalLink,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

// Helper function to get week range string (Monday to Sunday)
const getWeekRange = (weekStart) => {
  const monday = parseISO(weekStart);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return `${safeFormatDate(monday, 'dd.MM.')}–${safeFormatDate(sunday, 'dd.MM.')}`;
};

// Status badge styles
const getStatusStyle = (status) => {
  switch (status) {
    case 'genehmigt':
      return 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30';
    case 'eingereicht':
      return 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30';
    case 'abgelehnt':
      return 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30';
    case 'fehlt':
      return 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30';
    case 'laufend':
    case 'abwesend':
    default:
      return 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20';
  }
};

// Balance color helper
const getBalanceColor = (value) => {
  return value >= 0 
    ? 'text-emerald-600 dark:text-emerald-400' 
    : 'text-red-600 dark:text-red-400';
};

export default function TeamOverviewPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const canViewTimeAccount = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'accounting';

  const fetchTeamOverview = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/team-overview');
      setTeamData(response.data);
    } catch (err) {
      console.error('Failed to fetch team overview:', err);
      if (err.response?.status === 403) {
        setError('Für diese Ansicht fehlt die Berechtigung.');
      } else {
        toast.error('Fehler beim Laden der Team-Übersicht');
        setError('Fehler beim Laden der Daten');
      }
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchTeamOverview();
  }, [fetchTeamOverview]);

  // Calculate summary stats
  const clockedInCount = teamData.filter(p => p.clock.clocked_in).length;
  const totalCount = teamData.length;
  
  const missingWeeksCount = teamData.reduce((sum, person) => {
    return sum + person.weeks.filter(w => w.status === 'fehlt' || w.status === 'abgelehnt').length;
  }, 0);
  
  const totalBalance = teamData.reduce((sum, person) => sum + person.balance.total, 0);

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 md:p-8">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-4 md:p-8">
        <div className="text-center py-16">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{error}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Users2 className="w-8 h-8 text-indigo-400" />
            Team-Übersicht
          </h1>
          <p className="text-muted-foreground mt-1">
            Arbeitszeitkonten, Stempelstatus und Wocheneinreichungen
          </p>
        </div>
        <Button
          onClick={fetchTeamOverview}
          variant="outline"
          className="border-border text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </motion.div>

      {/* Info hint */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Info className="w-3.5 h-3.5" />
        <span>Wochen- und Monatswerte zählen abgeschlossene Tage bis einschließlich gestern. Der laufende Tag ist nicht enthalten.</span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span className="text-sm text-muted-foreground">Eingestempelt</span>
          </div>
          <p className="text-2xl font-mono font-bold text-foreground mt-2">
            {clockedInCount} <span className="text-sm font-normal text-muted-foreground">von {totalCount}</span>
          </p>
        </Card>
        <Card className="card-glass p-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span className="text-sm text-muted-foreground">Fehlende/abgelehnte Wochen</span>
          </div>
          <p className="text-2xl font-mono font-bold text-foreground mt-2">{missingWeeksCount}</p>
        </Card>
        <Card className="card-glass p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 text-indigo-400" />
            <span className="text-sm text-muted-foreground">Summe Saldo</span>
          </div>
          <p className={`text-2xl font-mono font-bold mt-2 ${getBalanceColor(totalBalance)}`}>
            {formatHoursToHM(totalBalance, true)}
          </p>
        </Card>
      </div>

      {/* Team Table */}
      {teamData.length === 0 ? (
        <div className="text-center py-16">
          <Users2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Keine Personen im sichtbaren Bereich.</h3>
        </div>
      ) : (
        <Card className="card-glass overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground font-medium">Status</TableHead>
                  <TableHead className="text-muted-foreground font-medium">Name</TableHead>
                  <TableHead className="text-muted-foreground font-medium text-right">Saldo</TableHead>
                  <TableHead className="text-muted-foreground font-medium text-right hidden sm:table-cell">Verplant</TableHead>
                  <TableHead className="text-muted-foreground font-medium text-right">Verfügbar</TableHead>
                  <TableHead className="text-muted-foreground font-medium text-right">Δ Woche</TableHead>
                  <TableHead className="text-muted-foreground font-medium text-right">Δ Monat</TableHead>
                  <TableHead className="text-muted-foreground font-medium text-right hidden sm:table-cell">Δ Vormonat</TableHead>
                  <TableHead className="text-muted-foreground font-medium">Wochen</TableHead>
                  {canViewTimeAccount && (
                    <TableHead className="text-muted-foreground font-medium text-right hidden sm:table-cell">Aktion</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {teamData.map((person, index) => (
                  <TableRow key={person.user_id} className="border-border">
                    {/* Status */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${person.clock.clocked_in ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {person.clock.clocked_in 
                            ? `seit ${formatTimeBerlin(person.clock.since)}` 
                            : 'nicht eingestempelt'}
                        </span>
                      </div>
                    </TableCell>
                    
                    {/* Name */}
                    <TableCell className="font-medium text-foreground whitespace-nowrap">
                      {person.last_name}, {person.first_name}
                    </TableCell>
                    
                    {/* Saldo gesamt */}
                    <TableCell className={`text-right font-mono ${getBalanceColor(person.balance.total)}`}>
                      {formatHoursToHM(person.balance.total, true)}
                    </TableCell>
                    
                    {/* Verplant */}
                    <TableCell className="text-right font-mono hidden sm:table-cell">
                      {person.balance.committed === 0 ? (
                        <span className="text-muted-foreground">–</span>
                      ) : (
                        formatHoursToHM(person.balance.committed)
                      )}
                    </TableCell>
                    
                    {/* Verfügbar */}
                    <TableCell className={`text-right font-mono ${getBalanceColor(person.balance.available)}`}>
                      {formatHoursToHM(person.balance.available, true)}
                    </TableCell>
                    
                    {/* Δ Woche */}
                    <TableCell className="text-right font-mono">
                      {person.period.this_week.days_counted === 0 ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground cursor-help">–</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Noch kein abgeschlossener Tag in dieser Woche</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className={getBalanceColor(person.period.this_week.balance)}>
                          {formatHoursToHM(person.period.this_week.balance, true)}
                        </span>
                      )}
                    </TableCell>
                    
                    {/* Δ Monat */}
                    <TableCell className="text-right font-mono">
                      {person.period.this_month.days_counted === 0 ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground cursor-help">–</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Noch kein abgeschlossener Tag in diesem Monat</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className={getBalanceColor(person.period.this_month.balance)}>
                          {formatHoursToHM(person.period.this_month.balance, true)}
                        </span>
                      )}
                    </TableCell>
                    
                    {/* Δ Vormonat */}
                    <TableCell className={`text-right font-mono hidden sm:table-cell ${getBalanceColor(person.period.last_month.balance)}`}>
                      {formatHoursToHM(person.period.last_month.balance, true)}
                    </TableCell>
                    
                    {/* Wochen */}
                    <TableCell>
                      <div className="flex gap-1">
                        {person.weeks.map((week, wIndex) => {
                          const weekNum = getISOWeek(parseISO(week.week_start));
                          const weekRange = getWeekRange(week.week_start);
                          const isAbsent = week.status === 'abwesend';
                          
                          return (
                            <TooltipProvider key={wIndex}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge
                                    variant="outline"
                                    className={`text-xs px-1.5 py-0.5 ${getStatusStyle(week.status)} ${isAbsent ? 'line-through' : ''}`}
                                  >
                                    KW{weekNum}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>KW {weekNum} · {weekRange} · {week.status}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                      </div>
                    </TableCell>
                    
                    {/* Aktion */}
                    {canViewTimeAccount && (
                      <TableCell className="text-right hidden sm:table-cell">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/team/time-account?user=${person.user_id}`)}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Arbeitszeitkonto öffnen</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-xs px-1.5 py-0.5 ${getStatusStyle('genehmigt')}`}>KW</Badge>
          <span>genehmigt</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-xs px-1.5 py-0.5 ${getStatusStyle('eingereicht')}`}>KW</Badge>
          <span>eingereicht</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-xs px-1.5 py-0.5 ${getStatusStyle('abgelehnt')}`}>KW</Badge>
          <span>abgelehnt</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-xs px-1.5 py-0.5 ${getStatusStyle('fehlt')}`}>KW</Badge>
          <span>fehlt</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-xs px-1.5 py-0.5 ${getStatusStyle('laufend')}`}>KW</Badge>
          <span>laufend</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className={`text-xs px-1.5 py-0.5 ${getStatusStyle('abwesend')} line-through`}>KW</Badge>
          <span>abwesend</span>
        </div>
      </div>
    </div>
  );
}
